import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { 
  Download, 
  Search, 
  Filter, 
  RefreshCw, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  User,
  MapPin
} from 'lucide-react';
import { auditService, AuditLogFilters, LoginAttempt } from '../api/services/auditService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { DashboardLayout } from '../components/layout/DashboardLayout';

const AuditLogs: React.FC = () => {
  const [filters, setFilters] = useState<AuditLogFilters>({
    startDate: format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    limit: 100,
    offset: 0
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [selectedSuccess, setSelectedSuccess] = useState<string>('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['auditLogs', filters],
    queryFn: () => auditService.getLoginLogs(filters),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  const { data: securityEvents } = useQuery({
    queryKey: ['securityEvents', filters.startDate, filters.endDate],
    queryFn: () => auditService.getSecurityEvents(filters.startDate, filters.endDate),
    refetchInterval: 30000
  });

  const handleFilterChange = (key: keyof AuditLogFilters, value: AuditLogFilters[keyof AuditLogFilters]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0 // Reset pagination when filters change
    }));
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const blob = await auditService.exportLogs({
        format,
        startDate: filters.startDate,
        endDate: filters.endDate,
        eventTypes: selectedEventType ? [selectedEventType] : undefined
      });
      auditService.downloadExport(blob, format);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'LOGIN_SUCCESS':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'LOGIN_FAILURE':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'ACCOUNT_LOCKED':
      case 'ACCOUNT_BANNED':
        return <Shield className="w-4 h-4 text-orange-500" />;
      case 'PASSWORD_CHANGE':
      case 'PASSWORD_RESET':
        return <User className="w-4 h-4 text-blue-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRiskBadge = (riskScore?: number) => {
    if (!riskScore) return null;
    const { level, color } = auditService.getRiskLevel(riskScore);
    
    const colorClasses = {
      red: 'bg-red-100 text-red-800',
      orange: 'bg-orange-100 text-orange-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      green: 'bg-green-100 text-green-800',
      gray: 'bg-gray-100 text-gray-800'
    };

    return (
      <Badge className={colorClasses[color as keyof typeof colorClasses]}>
        Risk: {level} ({riskScore})
      </Badge>
    );
  };

  const filteredLogs = data?.logs?.filter(log => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (
        !log.user_id?.email?.toLowerCase().includes(search) &&
        !log.email_domain?.toLowerCase().includes(search) &&
        !log.geo_location?.city?.toLowerCase().includes(search) &&
        !log.geo_location?.country?.toLowerCase().includes(search)
      ) {
        return false;
      }
    }
    if (selectedEventType && log.event_type !== selectedEventType) {
      return false;
    }
    if (selectedSuccess !== '' && log.success !== (selectedSuccess === 'true')) {
      return false;
    }
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Audit Logs</h1>
          <p className="text-gray-600">Monitor and review authentication events</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            variant="outline"
            onClick={() => handleExport('csv')}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            variant="outline"
            onClick={() => handleExport('json')}
          >
            <Download className="w-4 h-4 mr-2" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Security Alerts */}
      {securityEvents && securityEvents.total > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Security Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {securityEvents.events.slice(0, 3).map((event, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <span className="text-sm">
                    {auditService.getEventTypeLabel(event.event_type)} - {event.user_id?.email || 'Unknown'}
                  </span>
                  <Badge variant="destructive">
                    {event.severity || 'HIGH'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Event Type</label>
              <Select value={selectedEventType || "all"} onValueChange={(value) => setSelectedEventType(value === "all" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="LOGIN_SUCCESS">Successful Login</SelectItem>
                  <SelectItem value="LOGIN_FAILURE">Failed Login</SelectItem>
                  <SelectItem value="LOGOUT">Logout</SelectItem>
                  <SelectItem value="PASSWORD_CHANGE">Password Change</SelectItem>
                  <SelectItem value="PASSWORD_RESET">Password Reset</SelectItem>
                  <SelectItem value="ACCOUNT_LOCKED">Account Locked</SelectItem>
                  <SelectItem value="ACCOUNT_BANNED">Account Banned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <Select value={selectedSuccess || "all"} onValueChange={(value) => setSelectedSuccess(value === "all" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="true">Success</SelectItem>
                  <SelectItem value="false">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by email, domain, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Login Attempts</CardTitle>
          <CardDescription>
            Showing {filteredLogs?.length || 0} of {data?.total || 0} total logs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px]">
            <div className="space-y-2">
              {isLoading && (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                  <p className="mt-2 text-gray-600">Loading logs...</p>
                </div>
              )}
              
              {error && (
                <div className="text-center py-8">
                  <AlertTriangle className="w-8 h-8 mx-auto text-red-500" />
                  <p className="mt-2 text-red-600">Failed to load logs</p>
                </div>
              )}
              
              {filteredLogs?.map((log: LoginAttempt) => (
                <div 
                  key={log._id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getEventIcon(log.event_type)}
                        <span className="font-medium">
                          {auditService.getEventTypeLabel(log.event_type)}
                        </span>
                        {log.success ? (
                          <Badge className="bg-green-100 text-green-800">Success</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">Failed</Badge>
                        )}
                        {getRiskBadge(log.risk_score)}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">User:</span> {log.user_id?.email || 'Unknown'}
                        </div>
                        <div>
                          <span className="font-medium">Time:</span> {auditService.formatTimestamp(log.timestamp)}
                        </div>
                        {log.geo_location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {log.geo_location.city}, {log.geo_location.country}
                          </div>
                        )}
                        {log.failure_reason && (
                          <div>
                            <span className="font-medium">Reason:</span> {auditService.getFailureReasonLabel(log.failure_reason)}
                          </div>
                        )}
                      </div>
                      
                      {log.suspicious_indicators && log.suspicious_indicators.length > 0 && (
                        <div className="mt-2 flex gap-2">
                          {log.suspicious_indicators.map((indicator, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {indicator}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {log.response_time_ms && (
                      <div className="text-sm text-gray-500">
                        {log.response_time_ms}ms
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {!isLoading && filteredLogs?.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-600">No logs found matching your filters</p>
                </div>
              )}
            </div>
          </ScrollArea>
          
          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t">
              <Button
                variant="outline"
                disabled={filters.offset === 0}
                onClick={() => handleFilterChange('offset', Math.max(0, (filters.offset || 0) - (filters.limit || 100)))}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {data.page} of {data.totalPages}
              </span>
              <Button
                variant="outline"
                disabled={data.page >= data.totalPages}
                onClick={() => handleFilterChange('offset', (filters.offset || 0) + (filters.limit || 100))}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </DashboardLayout>
  );
};

export default AuditLogs;
